import { Container, IAppState } from 'app/containers/Container'
import { ResourceAction } from 'app/redux/Resources/actions';
import * as React from 'react'
import { Button, Col, Row } from 'react-bootstrap';

interface IReportPage {
  id: string
  title: string
  image: string
  description: string[]
}

interface ISampleReportState {
  isLoggedIn: boolean
  username: string
  key: string
  affixLocation: number
  samples: IReportPage[]
}

class SampleReport extends Container<{}, ISampleReportState> {

  samples = [
    {
      id: 'five_year',
      title: '5-Year Summary',
      image: '/assets/img/SampleReport5YearSummary.png',
      description: [
        'The 5-Year summary is a big picture overview of the foundation’s financial development over time.  This page offers a year by year recap of assets, incoming contributions, operating and administrative expenses, and charitable disbursements.',
        'Other data on this page includes a 5-Year summary of expenses, gifts and contributions by category.'
      ]
    },
    {
      id: 'performance_overview',
      title: 'Performance Overview',
      image: '/assets/img/SampleReportPerformacneOverview.png',
      description: [
        'This page summarizes the foundation\'s performance over 5 years by offering comparisons to the overall foundation universe and public market indices.',
        'Additionally, this page is a useful reference point for FoundationMark Star ratings, portfolio positioning, and disclosed investment expenses.'
      ]
    },
    {
      id: 'performance_vs_assets',
      title: 'Performance vs Asset Class',
      image: '/assets/img/SampleReportPerformancevsAssetClass.png',
      description: [
        'A question many foundation trustees may have is, How did we do versus the public markets?  This page is designed to show relative performance versus public market indices on a year by year basis as well as over the 3 and 5-year cumulative periods.',
        'The page includes the performance of the FoundationMark Index for the relevant time periods as well as a 60/40 Mix representing a traditional balanced portfolio.'
      ]
    },
    {
      id: 'star_rating',
      title: 'FoundationMark Star Rating',
      image: '/assets/img/SampleReportFoundationMarkStarRating.png',
      description: [
        'The FoundationMark Star Rating system provides an intuitive guide to relative performance versus other foundations with 5 stars as the highest rating and 1 star the lowest.',
        'FoundationMark provides two different comparison groups the first is versus all foundations, while the second is versus foundations with similar asset allocation profiles (see next page).'
      ]
    },
    {
      id: 'portfolio_positioning',
      title: 'Performance by Portfolio Positioning',
      image: '/assets/img/SampleReportPortfolioPositioning.png',
      description: [
        'Foundation asset allocations can range from very conservative consisting of high levels of cash and government bonds to more aggressive portfolios with high concentrations in growth assets such as stocks and other investments, and everywhere in between.  FoundationMark classifies foundations into four core portfolio styles based on their asset allocation.',
        'This page is designed to address questions arising from portfolio positioning decisions.  The top of the page shows the basis for the FoundationMark style categorizations. The middle of the page compares performance by portfolio type, while the bottom left pie chart shows the breakdown of portfolio styles by similar sized foundations.'
      ]
    },
    {
      id: 'peer_group_performance',
      title: 'Peer Group Performances',
      image: '/assets/img/SampleReportPeerGroupPerformance.png',
      description: [
        'FoundationMark builds representative peer groups based on a foundation’s asset allocation and asset level.',
        'This page shows the foundation’s performance versus its peer group over the past two years. Foundations in the top right quadrant outperformed their peers in both years, while those in the bottom left quadrant underperformed in both years.'
      ]
    },
    {
      id: 'investment_expenses',
      title: 'Disclosed Investment Expense',
      image: '/assets/img/SampleReportInvestmentExpenses.png',
      description: [
        'Most foundations incur costs related to managing their investment portfolios.  These expenses are recorded in the public filings and are used in determining excise fees for the foundation.',
        'FoundationMark is very deliberate in choosing the term “disclosed” as many foundations do not fully disclose their expense, resulting in higher excise fees.'
      ]
    },
    {
      id: 'financial_statements',
      title: 'Financial Statements',
      image: '/assets/img/SampleReportFinancialStatements.png',
      description: [
        'All the data used to estimate investment performance in the FoundationMark reports comes from public filings.',
        'The condensed financial statements are provided in the report to ensure transparency.'
      ]
    },
    {
      id: 'peer_list',
      title: 'Peer List',
      image: '/assets/img/SampleReportPeerList.png',
      description: [
        'A consistent question that FoundationMark is asked: Are there other foundations like us? Of course there are - here is a list.',
        'FoundationMark includes a list of foundations that share asset size and portfolio positioning with the subject foundation.'
      ]
    }
  ]

  state = {
    isLoggedIn: false,
    username: '',
    key: '',
    affixLocation: 150,
    samples: this.samples
  }

  mapAppStateToContainerState(appState: IAppState): ISampleReportState {
    const isLoggedIn = !!appState.User.username
    const username = appState.User.username
    const key = appState.User.token
    return { isLoggedIn, username, key, affixLocation: 0, samples: this.samples }
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll.bind(this));
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    const affixLocation = window.scrollY > 100 ? 10 : 150
    this.setState({ affixLocation })
  }

  downloadIndex() {
    const fileName = 'FoundationMarkSample.pdf'
    if (this.state.isLoggedIn) {
      //NOTE: download file
      ResourceAction.getFileUrl(this.state.username, this.state.key, fileName)
        .then((response) => {
          window.open(response.resource.url)
        })
        .catch((error) => {
          // tslint:disable-next-line:no-console
          console.log(error)
        })
    } else {
      //NOTE: Redirect to login page
      location.hash = '#/login?fileName=' + fileName + '&page=reports'
    }
  }

  scrollToSection(e) {
    e.preventDefault();
    let id = e.currentTarget.id
    id = id.replace('_link', '')
    document.getElementById(id).scrollIntoView()
  }

  render() {
    const { affixLocation } = this.state

    const topOftoc = affixLocation === 0 ? 150 : affixLocation.toString() + 'px'
    return (
      <section className="margin-bottom-lg">
        <div className="container animated fadeInRight">
          <h3>Selected Pages from Sample Report</h3>
        </div>
        <div className="container animated fadeInRight">
          <Row>
            <Col sm={12} md={12} lg={9}>
              <div>
                {
                  this.state.samples.map((sample) => {
                    return (
                      <Row id={sample.id} className="margin-y-sm">
                        <Col md={4}>
                          <p>
                            <b style={{ fontSize: "20px" }}>{sample.title}</b>
                          </p>
                          <p>
                            <ul>
                              {
                                sample.description.map((bulletPoint) => {
                                  return (
                                    <li>{bulletPoint}</li>
                                  )
                                })
                              }
                            </ul>
                          </p>
                        </Col>
                        <Col md={8}><img src={sample.image} width="100%" /></Col>
                      </Row>
                    )
                  })
                }
              </div>
            </Col>
            <Col style={{ left: '70%', top: topOftoc, }} className="affix" xsHidden smHidden mdHidden lg={3}>
              <ul style={{ borderLeft: '1px solid #666' }} id="tofc">
                {
                  this.state.samples.map((sample) => {
                    const link = sample.id + '_link'
                    return (
                      <li><a style={{ cursor: 'pointer' }} id={link} onClick={this.scrollToSection}>{sample.title}</a></li>
                    )
                  })
                }
              </ul>
            </Col>
          </Row>
        </div>
      </section>
    )
  }
}

export {
  SampleReport
}



// WEBPACK FOOTER //
// ./src/app/containers/SampleReport/index.tsx